import React from "react";
import { MenuItem, Spinner, Tag } from "@blueprintjs/core";
import { Suggest } from "@blueprintjs/select";
import _ from "lodash";

class SingleSelectWrapper extends React.Component {
    constructor(props) {
        super(props);
        this.suggestRef = React.createRef();
    }

    state = {
        item: null,
    };

    renderItem = (item, { handleClick, modifiers, query }) => {
        if (!modifiers.matchesPredicate) {
            return null;
        }
        let label = null;
        const itemSample = this.props.items[0];
        if (typeof itemSample === "string") {
            // do nothing!
        } else if (typeof itemSample === "object") {
            const itemNow = _.find(this.props.items, (each) => each.title === item);
            label = itemNow.type;
        }
        return (
            <MenuItem
                className="axis"
                active={modifiers.active}
                /* icon={this.isItemSelected(item) ? "tick" : "blank"} */
                disabled={modifiers.disabled}
                key={item}
                onClick={handleClick}
                text={this.highlightText(item, query)}
                shouldDismissPopover={false}
                label={label}
            />
        );
    };

    escapeRegExpChars = (text) => {
        return text.replace(/([.*+?^=!:${}()|[\]/\\])/g, "\\$1");
    };

    highlightText = (text, query) => {
        let lastIndex = 0;
        const words = query
            .split(/\s+/)
            .filter((word) => word.length > 0)
            .map(this.escapeRegExpChars);
        if (words.length === 0) {
            return [text];
        }
        const regexp = new RegExp(words.join("|"), "gi");
        const tokens = [];
        while (true) {
            const match = regexp.exec(text);
            if (!match) {
                break;
            }
            const length = match[0].length;
            const before = text.slice(lastIndex, regexp.lastIndex - length);
            if (before.length > 0) {
                tokens.push(before);
            }
            lastIndex = regexp.lastIndex;
            tokens.push(<strong key={lastIndex}>{match[0]}</strong>);
        }
        const rest = text.slice(lastIndex);
        if (rest.length > 0) {
            tokens.push(rest);
        }
        return tokens;
    };

    handleItemSelect = (item) => {
        const itemSample = this.props.items[0];
        if (typeof itemSample === "string") {
            this.setState({ item: item });
            this.props.onChange(item);
        } else if (typeof itemSample === "object") {
            const retItem = _.find(this.props.items, (each) => each.title === item);
            this.setState({ item: item });
            this.props.onChange(retItem);
        } else {
            this.props.onChange("ITEM_INCOMPATIBLE_ERROR");
        }
    };

    filterItem = (query, item) => {
        /** Implement Any Random Search Here which returns a Boolean */
        /** Correspondingly IF you want aesthetics, change `highlightText` */
        if (!query || query.trim() === "") return true;
        const normalizedQuery = query.toLowerCase().trim();
        const normalizedItem = (item || "").toLowerCase();
        return normalizedItem.indexOf(normalizedQuery) >= 0;
    };

    filterItemList = (query, items) => {
        /** Filter the entire list at once - more reliable than itemPredicate */
        if (!query || query.trim() === "") return items;
        const normalizedQuery = query.toLowerCase().trim();
        return items.filter(item => {
            const normalizedItem = (item || "").toLowerCase();
            return normalizedItem.indexOf(normalizedQuery) >= 0;
        });
    };

    // Method to focus on the input
    focus = () => {
        if (this.suggestRef.current && this.suggestRef.current.querySelector) {
            const input = this.suggestRef.current.querySelector("input");
            if (input) {
                input.focus();
            }
        }
    };

    resolveSelectedItem = () => {
        // If caller wants a fully controlled component, always honor the prop (including null to clear).
        if (this.props.showSelectProp) {
            return this.props.selectedItem;
        }
        // If selectedItem prop is explicitly provided (even null), treat as controlled.
        if (Object.prototype.hasOwnProperty.call(this.props, "selectedItem")) {
            return this.props.selectedItem;
        }
        // Otherwise fall back to internal state for backward compatibility.
        return this.state.item || null;
    };

    render() {
        const {
            loading = false,
            emptyMessage = "No Items To Search",
            loadingMessage = "Loading options...",
        } = this.props;
        if (!this.props.items || !this.props.items.length) {
            if (loading) {
                return (
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "4px 6px",
                        }}
                    >
                        <Spinner size={16} intent="primary" />
                        <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                            {loadingMessage}
                        </span>
                    </div>
                );
            }
            return <Tag minimal>{emptyMessage}</Tag>;
        }
        let inputProps = { style: { minWidth: "6em" } };
        if (this.props.placeholder) {
            inputProps["placeholder"] = this.props.placeholder;
        }
        if (this.props.disabled) {
            inputProps["disabled"] = true;
        }
        if (this.props.width) {
            inputProps["style"] = { width: this.props.width };
        }
        let items = [];
        const itemSample = this.props.items[0];
        if (typeof itemSample === "string") {
            // do nothing!
            items = this.props.items;
        } else if (typeof itemSample === "object") {
            items = this.props.items.map((each) => each.title);
        } else {
            return <Tag minimal>Incompatible Item Types</Tag>;
        }

        const noResultsText = this.props.noResultsText || "No results.";
        const itemListPredicate = this.props.itemListPredicate || this.filterItemList;

        return (
            <div ref={this.suggestRef}>
                <Suggest
                    style={{ minWidth: "4em" }}
                    className="axis"
                    inputProps={{ ...inputProps, ...(this.props.inputProps || {}) }}
                    itemListPredicate={itemListPredicate}
                    items={items}
                    noResults={
                        <MenuItem disabled={true} text={noResultsText} className="axia" />
                    }
                    itemRenderer={this.props.itemRenderer || this.renderItem}
                    selectedItem={this.resolveSelectedItem()}
                    onItemSelect={this.handleItemSelect}
                    inputValueRenderer={(item) => item}
                    popoverProps={{ minimal: true, ...(this.props.popoverProps || {}) }}
                    resetOnSelect={false}
                    resetOnClose={false}
                />
            </div>
        );
    }
}

export default SingleSelectWrapper;
